import React, { useState, useEffect } from 'react';
import { Menu, Bell, ChevronDown, LogOut, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi, notificationsApi } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import EditProfilDialog from './EditProfilDialog';

const READ_KEY = 'sipekaes_notif_read';
const loadReadIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY)) || []); } catch { return new Set(); }
};
const saveReadIds = (ids) => {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...ids])); } catch { /* ignore */ }
};

const timeAgo = (iso) => {
  if (!iso) return '-';
  const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return 'Baru saja';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} menit lalu`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} jam lalu`;
  return `${Math.floor(diffSec / 86400)} hari lalu`;
};

const Topbar = ({ title, subtitle, onToggle, onMobileToggle }) => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [profilOpen, setProfilOpen] = useState(false);
  const [readIds, setReadIds] = useState(loadReadIds);
  const [notifItems, setNotifItems] = useState([]);

  const loadNotifs = () => { notificationsApi.list().then(r => setNotifItems(r.data)).catch(() => {}); };
  useEffect(() => { loadNotifs(); }, []);

  const notifs = notifItems.map(n => ({ ...n, unread: !readIds.has(n.id) }));
  const unread = notifs.filter(n => n.unread).length;

  const markRead = (id) => {
    setReadIds(prev => {
      const next = new Set(prev).add(id);
      saveReadIds(next);
      return next;
    });
  };
  const markAllRead = () => {
    setReadIds(prev => {
      const next = new Set(prev);
      notifItems.forEach(n => next.add(n.id));
      saveReadIds(next);
      return next;
    });
  };
  const openNotif = (n) => {
    markRead(n.id);
    if (n.route && n.record_id) navigate(`${n.route}?open=${n.record_id}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpdateProfil = async (data) => {
    try {
      const res = await authApi.updateMe(data);
      updateUser(res.data);
      toast({ title: 'Profil berhasil diperbarui' });
      return true;
    } catch (e) {
      toast({ title: 'Gagal memperbarui profil', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  return (
    <header className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between border-b border-gray-100 gap-2">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {/* Mobile hamburger */}
        <button onClick={onMobileToggle} className="lg:hidden p-1.5 rounded-lg hover:bg-orange-50 text-gray-600 hover:text-orange-600 transition-colors">
          <Menu className="w-4.5 h-4.5" />
        </button>
        {/* Desktop collapse */}
        <button onClick={onToggle} className="hidden lg:block p-1.5 rounded-lg hover:bg-orange-50 text-gray-600 hover:text-orange-600 transition-colors">
          <Menu className="w-4.5 h-4.5" />
        </button>
        <div className="min-w-0 flex-1 hidden md:block">
          <div className="flex items-center gap-2.5">
            {/* Pemilih Wilayah */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-50/80 border border-orange-200/80 text-xs font-bold text-orange-950 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
              <span>Kabupaten Sukabumi</span>
            </div>

            {/* Input Pencarian Universal */}
            <div className="relative flex-1 max-w-sm">
              <input
                type="text"
                placeholder="Cari nama, wilayah, atau kategori..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    navigate(`/partisipan?q=${encodeURIComponent(e.target.value.trim())}`);
                  }
                }}
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-gray-50 border border-gray-200 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-500/20 focus:border-orange-500 text-gray-800 placeholder-gray-400 h-8"
              />
              <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Notifications */}
        <DropdownMenu onOpenChange={(open) => open && loadNotifs()}>
          <DropdownMenuTrigger className="relative p-1.5 sm:p-2 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors">
            <Bell className="w-4 h-4 text-gray-600" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {unread}
              </span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 sm:w-80">
            <div className="flex items-center justify-between px-2">
              <DropdownMenuLabel className="px-0 text-xs">Notifikasi</DropdownMenuLabel>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[11px] font-semibold text-orange-600 hover:text-orange-700">
                  Tandai semua dibaca
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
            {notifs.length === 0 && (
              <p className="text-xs text-gray-400 font-medium text-center py-4">Tidak ada notifikasi</p>
            )}
            {notifs.map(n => (
              <DropdownMenuItem key={n.id} className="flex-col items-start py-2.5 cursor-pointer" onClick={() => openNotif(n)}>
                <div className="flex items-center gap-2 w-full">
                  {n.unread && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />}
                  <span className="font-semibold text-xs">{n.title}</span>
                </div>
                <span className="text-[11px] text-gray-500 ml-3.5">{n.desc} · {timeAgo(n.time)}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 pl-1 sm:pl-1.5 pr-2 sm:pr-3 py-1 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors">
            <img src={user?.avatar} alt={user?.name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover" />
            <div className="text-left hidden sm:block">
              <p className="text-xs font-bold text-gray-900 leading-tight truncate max-w-[130px]">{user?.name}</p>
              <p className="text-[10px] text-orange-600 font-semibold truncate max-w-[130px]">{user?.roleLabel}</p>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden sm:block" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="sm:hidden">
              <p className="text-sm font-bold">{user?.name}</p>
              <p className="text-xs text-orange-600 font-semibold">{user?.roleLabel}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="sm:hidden" />
            <DropdownMenuLabel className="hidden sm:block">Akun Saya</DropdownMenuLabel>
            <DropdownMenuSeparator className="hidden sm:block" />
            <DropdownMenuItem onClick={() => setProfilOpen(true)}><User className="w-4 h-4 mr-2" /> Profil</DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-600"><LogOut className="w-4 h-4 mr-2" /> Keluar</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <EditProfilDialog
        open={profilOpen}
        onOpenChange={setProfilOpen}
        user={user}
        onSave={handleUpdateProfil}
      />
    </header>
  );
};

export default Topbar;
